import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronRight, Copy, MapPin, Package, Phone, RefreshCw, Search, Store, Truck, User, X } from 'lucide-react';

const STATUS_LABELS = {
  pending: 'Pendiente',
  encamino: 'En camino',
  entregado: 'Entregado',
  cancelado: 'Cancelado',
  rechazado: 'Rechazado',
  postergado: 'Postergado',
};

function PossibleRepetitionsModal({ groups, selectedDate, isLoading, hasError, onRefresh, onClose, onSelectPoint }) {
  const [expandedKey, setExpandedKey] = useState(null);
  const [search, setSearch] = useState('');
  const closeButtonRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    closeButtonRef.current?.focus();
    const handleEscape = (event) => {
      if (event.key === 'Escape') onCloseRef.current();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return groups;
    return groups.filter((group) => [
      group.phone,
      group.product,
      ...group.points.flatMap((point) => [
        point.id,
        point.recipient_name,
        point.recipient_phone,
        point.username,
        point.address,
      ]),
    ].some((value) => String(value || '').toLowerCase().includes(term)));
  }, [groups, search]);

  const pointCount = groups.reduce((total, group) => total + group.points.length, 0);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-2 font-montserrat sm:p-4">
      <div role="dialog" aria-modal="true" aria-labelledby="possible-repetitions-title" className="flex max-h-[calc(100dvh-1rem)] min-h-0 w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl sm:max-h-[90dvh]">
        <div className="flex shrink-0 items-start justify-between gap-3 bg-[#B1336E] px-4 py-4 text-white sm:px-6">
          <div className="flex min-w-0 items-start gap-3">
            <Copy className="mt-0.5 h-6 w-6 shrink-0" />
            <div>
              <h2 id="possible-repetitions-title" className="text-lg font-bold sm:text-xl">Posibles repeticiones</h2>
              <p className="text-xs text-white/85 sm:text-sm">{selectedDate} · {groups.length} grupos · {pointCount} puntos</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={onRefresh} disabled={isLoading && !hasError} aria-label="Actualizar posibles repeticiones" title="Actualizar datos del día" className="rounded-md p-1 hover:bg-white/20 disabled:opacity-50">
              <RefreshCw className="h-5 w-5" />
            </button>
            <button ref={closeButtonRef} type="button" onClick={onClose} aria-label="Cerrar posibles repeticiones" className="rounded-md p-1 hover:bg-white/20">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="shrink-0 border-b border-slate-200 px-4 py-3 sm:px-6">
          <p className="mb-3 text-xs text-slate-600">
            Coincidencias del mismo día por teléfono y producto. Pueden ser entregas legítimas; verifica el contexto antes de actuar.
          </p>
          {hasError && <p role="alert" className="mb-3 rounded-md bg-red-50 p-2 text-xs text-red-700">No se pudieron actualizar los puntos de este día. Intenta de nuevo; los resultados visibles pueden estar desactualizados.</p>}
          {isLoading && !hasError && <p role="status" className="mb-3 text-xs text-slate-500">Cargando puntos del día...</p>}
          {groups.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                type="search"
                aria-label="Buscar posibles repeticiones"
                placeholder="Buscar teléfono, producto, nombre, tienda o ID"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm outline-none focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20"
              />
            </div>
          )}
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-slate-50 p-3 sm:p-5">
          {groups.length === 0 && isLoading && !hasError ? (
            <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-600">Cargando posibles repeticiones...</div>
          ) : groups.length === 0 && hasError ? (
            <div className="rounded-lg border border-red-200 bg-white p-8 text-center text-sm text-red-700">No se pudo comprobar si hay repeticiones para esta fecha.</div>
          ) : groups.length === 0 ? (
            <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-600">
              No hay puntos con el mismo teléfono y producto para esta fecha.
            </div>
          ) : filteredGroups.length === 0 ? (
            <div className="rounded-lg border border-slate-200 bg-white p-8 text-center text-sm text-slate-600">
              No se encontraron grupos con esa búsqueda.
            </div>
          ) : filteredGroups.map((group) => {
            const expanded = expandedKey === group.key;
            return (
              <section key={group.key} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                <button
                  type="button"
                  aria-expanded={expanded}
                  onClick={() => setExpandedKey(expanded ? null : group.key)}
                  className="flex w-full items-start justify-between gap-3 p-4 text-left hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#B1336E]"
                >
                  <div className="min-w-0 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2 text-sm font-bold text-slate-800">
                      <Phone className="h-4 w-4 shrink-0 text-[#B1336E]" />
                      {group.phone}
                      <span className="rounded-full bg-[#B1336E]/10 px-2 py-0.5 text-xs text-[#B1336E]">{group.points.length} puntos</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm text-slate-700">
                      <Package className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                      <span className="break-words">{group.product}</span>
                    </div>
                    <div className="flex items-start gap-2 text-xs text-slate-600">
                      <User className="h-4 w-4 shrink-0 text-slate-400" />
                      <span className="break-words">{group.names.length ? group.names.join(' · ') : 'Sin nombre'}</span>
                    </div>
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      {group.activeCount > 1 && <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">{group.activeCount} puntos activos</span>}
                      {group.namesDiffer && <span className="inline-flex items-center gap-1 rounded bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-800"><AlertTriangle className="h-3 w-3" /> Nombres diferentes</span>}
                      {group.storesDiffer && <span className="rounded bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-800">Tiendas diferentes</span>}
                    </div>
                  </div>
                  {expanded ? <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" /> : <ChevronRight className="h-5 w-5 shrink-0 text-slate-500" />}
                </button>

                {expanded && (
                  <div className="space-y-2 border-t border-slate-200 bg-slate-50 p-3">
                    {group.points.map((point) => (
                      <button
                        key={point.id}
                        type="button"
                        onClick={() => onSelectPoint(point.id)}
                        aria-label={`Abrir detalle del punto ${point.id}`}
                        className="w-full rounded-md border border-slate-200 bg-white p-3 text-left transition-colors hover:border-[#B1336E]/50 hover:bg-[#B1336E]/5 focus:outline-none focus:ring-2 focus:ring-[#B1336E]"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-sm font-bold text-[#B1336E]">#{point.id}</span>
                            <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">{STATUS_LABELS[point.status] || point.status || 'Sin estado'}</span>
                          </div>
                          <span className="text-xs font-semibold text-[#B1336E]">Abrir detalle →</span>
                        </div>
                        <div className="mt-2 grid gap-x-4 gap-y-1.5 text-xs text-slate-600 sm:grid-cols-2">
                          <span className="flex items-start gap-1.5"><User className="h-3.5 w-3.5 shrink-0" />{point.recipient_name || 'Sin nombre'}</span>
                          <span className="flex items-start gap-1.5"><Phone className="h-3.5 w-3.5 shrink-0" />{point.recipient_phone}</span>
                          <span className="flex items-start gap-1.5"><Store className="h-3.5 w-3.5 shrink-0" />{point.username || (point.user_id != null ? `Tienda #${point.user_id}` : 'Tienda no identificada')}</span>
                          <span className="flex items-start gap-1.5 sm:col-span-2"><MapPin className="h-3.5 w-3.5 shrink-0" />{[point.address, point.district].filter(Boolean).join(' · ') || 'Sin dirección'}</span>
                          {point.driver_id && <span className="flex items-start gap-1.5"><Truck className="h-3.5 w-3.5 shrink-0" />{point.driver?.username || `Conductor #${point.driver_id}`}</span>}
                          {point.product !== group.product && <span className="flex items-start gap-1.5"><Package className="h-3.5 w-3.5 shrink-0" />{point.product}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PossibleRepetitionsModal;
